import React, { useState, useEffect } from 'react';
import {
  Plus, Play, Edit, Trash2, Clock, CheckCircle, XCircle, AlertCircle,
  RefreshCw, Power, GitBranch, Eye, Copy, BarChart3
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTenantProtection } from '../../../hooks/useTenantProtection';
import WorkflowEditor from './WorkflowEditor';
import WorkflowViewer from './WorkflowViewer';

const WorkflowList = ({ onStatsChange }) => {
  const { getProtectedTenantCode } = useTenantProtection();

  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [executingWorkflowId, setExecutingWorkflowId] = useState(null);
  const [viewingWorkflow, setViewingWorkflow] = useState(null);

  useEffect(() => {
    loadWorkflows();
  }, []);

  const loadWorkflows = async () => {
    try {
      setLoading(true);
      const tenantCode = getProtectedTenantCode();
      const response = await axios.get('/api/automation/workflows', {
        headers: { 'X-Tenant-Code': tenantCode },
      });
      setWorkflows(response.data);
    } catch (error) {
      console.error('Failed to load workflows:', error);
      toast.error('Failed to load workflows');
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteWorkflow = async (workflow) => {
    if (!window.confirm(`Execute workflow "${workflow.WorkflowName}"?`)) {
      return;
    }

    try {
      setExecutingWorkflowId(workflow.WorkflowID);
      const tenantCode = getProtectedTenantCode();
      const response = await axios.post(
        `/api/automation/workflows/${workflow.WorkflowID}/execute`,
        {},
        { headers: { 'X-Tenant-Code': tenantCode } }
      );
      toast.success('Workflow execution started');
      loadWorkflows();
    } catch (error) {
      console.error('Failed to execute workflow:', error);
      toast.error(error.response?.data?.error || 'Failed to execute workflow');
    } finally {
      setExecutingWorkflowId(null);
    }
  };

  const handleToggleWorkflow = async (workflow) => {
    try {
      const tenantCode = getProtectedTenantCode();
      await axios.put(
        `/api/automation/workflows/${workflow.WorkflowID}`,
        { isEnabled: !workflow.IsEnabled },
        { headers: { 'X-Tenant-Code': tenantCode } }
      );
      toast.success(`Workflow ${!workflow.IsEnabled ? 'enabled' : 'disabled'}`);
      loadWorkflows();
      if (onStatsChange) onStatsChange();
    } catch (error) {
      console.error('Failed to toggle workflow:', error);
      toast.error('Failed to update workflow');
    }
  };

  const handleDeleteWorkflow = async (workflow) => {
    if (!window.confirm(`Are you sure you want to delete the workflow "${workflow.WorkflowName}"?`)) {
      return;
    }

    try {
      const tenantCode = getProtectedTenantCode();
      await axios.delete(`/api/automation/workflows/${workflow.WorkflowID}`, {
        headers: { 'X-Tenant-Code': tenantCode },
      });
      toast.success('Workflow deleted');
      loadWorkflows();
      if (onStatsChange) onStatsChange();
    } catch (error) {
      console.error('Failed to delete workflow:', error);
      toast.error(error.response?.data?.error || 'Failed to delete workflow');
    }
  };

  const handleDuplicateWorkflow = async (workflow) => {
    try {
      const tenantCode = getProtectedTenantCode();
      await axios.post(
        `/api/automation/workflows/${workflow.WorkflowID}/duplicate`,
        {},
        { headers: { 'X-Tenant-Code': tenantCode } }
      );
      toast.success('Workflow duplicated');
      loadWorkflows();
      if (onStatsChange) onStatsChange();
    } catch (error) {
      console.error('Failed to duplicate workflow:', error);
      toast.error(error.response?.data?.error || 'Failed to duplicate workflow');
    }
  };

  const handleEditWorkflow = (workflow) => {
    setEditingWorkflow(workflow);
    setShowEditor(true);
  };

  const handleCreateNew = () => {
    setEditingWorkflow(null);
    setShowEditor(true);
  };

  const handleCloseEditor = () => {
    setShowEditor(false);
    setEditingWorkflow(null);
    loadWorkflows();
    if (onStatsChange) onStatsChange();
  };

  const handleViewWorkflow = (workflow) => {
    setViewingWorkflow(workflow);
  };

  const handleCloseViewer = () => {
    setViewingWorkflow(null);
    loadWorkflows();
  };

  if (showEditor) {
    return (
      <WorkflowEditor
        workflow={editingWorkflow}
        onClose={handleCloseEditor}
      />
    );
  }

  if (viewingWorkflow) {
    return (
      <WorkflowViewer
        workflow={viewingWorkflow}
        onClose={handleCloseViewer}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Workflows</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Chain jobs together with conditional logic
          </p>
        </div>
        <button
          onClick={handleCreateNew}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-600 transition-colors duration-200"
        >
          <Plus className="h-5 w-5 mr-2" />
          Create Workflow
        </button>
      </div>

      {/* Workflows List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-teal-500" />
        </div>
      ) : workflows.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <GitBranch className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No workflows</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Get started by creating a new workflow
          </p>
          <div className="mt-6">
            <button
              onClick={handleCreateNew}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-600"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create Workflow
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {workflows.map((workflow) => (
            <div
              key={workflow.WorkflowID}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors duration-200 hover:shadow-md"
            >
              <div className="p-6">
                {/* Workflow Header */}
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <GitBranch className="h-5 w-5 text-teal-500 dark:text-teal-400 flex-shrink-0" />
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {workflow.WorkflowName}
                      </h3>
                      {workflow.IsEnabled ? (
                        <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-5 w-5 text-gray-400 dark:text-gray-600 flex-shrink-0" />
                      )}
                    </div>
                    {workflow.Description && (
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                        {workflow.Description}
                      </p>
                    )}
                  </div>
                </div>

                {/* Workflow Stats */}
                <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-gray-500 dark:text-gray-400">Steps</div>
                    <div className="mt-1 font-semibold text-gray-900 dark:text-gray-100">
                      {workflow.StepCount || 0}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500 dark:text-gray-400">Success Rate</div>
                    <div className="mt-1 font-semibold text-gray-900 dark:text-gray-100">
                      {workflow.SuccessRate !== null && workflow.SuccessRate !== undefined
                        ? `${Math.round(workflow.SuccessRate)}%`
                        : 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500 dark:text-gray-400">Executions</div>
                    <div className="mt-1 font-semibold text-gray-900 dark:text-gray-100">
                      {workflow.ExecutionCount || 0}
                    </div>
                  </div>
                </div>

                {/* Last Execution */}
                {workflow.LastExecutedAt && (
                  <div className="mt-4 flex items-center text-xs text-gray-500 dark:text-gray-400">
                    <Clock className="h-4 w-4 mr-1" />
                    Last run: {new Date(workflow.LastExecutedAt).toLocaleString()}
                  </div>
                )}

                {/* Actions */}
                <div className="mt-6 flex items-center space-x-2">
                  <button
                    onClick={() => handleExecuteWorkflow(workflow)}
                    disabled={executingWorkflowId === workflow.WorkflowID || !workflow.IsEnabled}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    <Play className="h-4 w-4 mr-1.5" />
                    Execute
                  </button>
                  <button
                    onClick={() => handleViewWorkflow(workflow)}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200"
                  >
                    <BarChart3 className="h-4 w-4 mr-1.5" />
                    View
                  </button>
                  <button
                    onClick={() => handleEditWorkflow(workflow)}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200"
                  >
                    <Edit className="h-4 w-4 mr-1.5" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleToggleWorkflow(workflow)}
                    className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium transition-colors duration-200 ${
                      workflow.IsEnabled
                        ? 'border-yellow-300 dark:border-yellow-600 text-yellow-700 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 hover:bg-yellow-100 dark:hover:bg-yellow-900/30'
                        : 'border-green-300 dark:border-green-600 text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30'
                    }`}
                  >
                    <Power className="h-4 w-4 mr-1.5" />
                    {workflow.IsEnabled ? 'Disable' : 'Enable'}
                  </button>
                  <button
                    onClick={() => handleDuplicateWorkflow(workflow)}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200"
                    title="Duplicate workflow"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteWorkflow(workflow)}
                    className="inline-flex items-center px-3 py-2 border border-red-300 dark:border-red-600 rounded-md text-sm font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors duration-200"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WorkflowList;

